/**
 * Mango API Service Layer
 * 
 * This is the centralized API service for all Mango platform requests.
 * All frontend components should use these services instead of hardcoded data.
 * Production (non-localhost) always uses live API; no hardcoded dev data when go-live.
 *
 * IMPORTANT: The correct production API is https://api.mango-business.solutions
 * Do NOT use api.mango.app - that domain does not resolve (ERR_NAME_NOT_RESOLVED).
 */
const LIVE_API_URL = 'https://api.mango-business.solutions/api/v1';
const DEV_API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

const API_BASE_URL = ((): string => {
  if (typeof window === 'undefined') return process.env.REACT_APP_API_URL || LIVE_API_URL;
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return DEV_API_URL;
  return process.env.REACT_APP_API_URL || LIVE_API_URL;
})();

// Log API URL at startup (dev only) - helps debug wrong api.mango.app errors
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  console.log('[Mango Personal] API_BASE_URL:', API_BASE_URL);
  if (API_BASE_URL.includes('mango.app')) {
    console.warn('[Mango Personal] WARNING: api.mango.app does not resolve! Use api.mango-business.solutions or localhost:4000. Check .env for REACT_APP_API_URL.');
  }
}

export { API_BASE_URL };

// mango-chat's own origin, for deep links out of this app (e.g. "View in chat" on a calendar
// item). Same hostname-fallback shape as API_BASE_URL above — set REACT_APP_MANGO_CHAT_URL once
// mango-chat has a real dev/preview or production origin.
const DEV_MANGO_CHAT_URL = process.env.REACT_APP_MANGO_CHAT_URL || 'http://localhost:3001';
const LIVE_MANGO_CHAT_URL = process.env.REACT_APP_MANGO_CHAT_URL || 'https://chat.mango-business.solutions';
export const MANGO_CHAT_URL = ((): string => {
  if (typeof window === 'undefined') return LIVE_MANGO_CHAT_URL;
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return DEV_MANGO_CHAT_URL;
  return LIVE_MANGO_CHAT_URL;
})();

// Types for API responses
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
  pagination?: {
    page: number;
    perPage: number;
    total: number;
    totalPages: number;
  };
}

export interface SearchFilters {
  query?: string;
  category?: string;
  subcategory?: string;
  minPrice?: number;
  maxPrice?: number;
  location?: {
    lat: number;
    lng: number;
    radiusKm: number;
  };
  attributes?: Record<string, any>;
  sortBy?: 'relevance' | 'price_asc' | 'price_desc' | 'rating' | 'distance' | 'newest';
  page?: number;
  perPage?: number;
}

// ==================== AUTH API ====================
export const authApi = {
  login: async (email: string, password: string) => {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    return response.json();
  },

  register: async (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phone?: string;
  }) => {
    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  logout: async () => {
    const response = await fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  refreshToken: async (refreshToken: string) => {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    return response.json();
  },

  getProfile: async () => {
    const response = await fetch(`${API_BASE_URL}/auth/me`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  updateProfile: async (data: { firstName?: string; lastName?: string; phone?: string | null; staffProfile?: Record<string, unknown> | object }) => {
    const response = await fetch(`${API_BASE_URL}/auth/profile`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },

  forgotPassword: async (email: string) => {
    const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    return response.json();
  },

  resetPassword: async (token: string, email: string, newPassword: string) => {
    const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, email, newPassword }),
    });
    return response.json();
  },
};

// ==================== WALLET API ====================
// Uses mango-coin routes (MangoWallet) - wallet.routes uses legacy prisma.wallet which may not exist
export const walletApi = {
  getBalance: async () => {
    const response = await fetch(`${API_BASE_URL}/mango-coin/wallet/balance`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  getTransactions: async (page = 1, perPage = 20) => {
    const response = await fetch(
      `${API_BASE_URL}/mango-coin/transactions?page=${page}&limit=${perPage}`,
      { headers: getAuthHeaders() }
    );
    return response.json();
  },

  sendCredits: async (receiverAddressOrMangoId: string, amount: number, note?: string) => {
    const response = await fetch(`${API_BASE_URL}/mango-coin/transfer`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        receiverAddress: receiverAddressOrMangoId,
        amount,
        description: note,
      }),
    });
    return response.json();
  },

  requestCredits: async (fromUserId: string, amount: number, note?: string) => {
    const response = await fetch(`${API_BASE_URL}/wallet/request`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ fromUserId, amount, note }),
    });
    return response.json();
  },

  getRequests: async () => {
    const response = await fetch(`${API_BASE_URL}/wallet/requests`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  respondToRequest: async (requestId: string, accept: boolean) => {
    const response = await fetch(`${API_BASE_URL}/wallet/requests/${requestId}/respond`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ accept }),
    });
    return response.json();
  },

  cancelRequest: async (requestId: string) => {
    const response = await fetch(`${API_BASE_URL}/wallet/requests/${requestId}/cancel`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  // Alias for sendCredits - uses mango-coin/transfer (receiverAddress or Mango ID)
  send: async (receiverAddressOrMangoId: string, amount: number, note?: string) => {
    const response = await fetch(`${API_BASE_URL}/mango-coin/transfer`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        receiverAddress: receiverAddressOrMangoId,
        amount,
        description: note,
      }),
    });
    return response.json();
  },
};

// ==================== TRUST SCORE API ====================
export const trustScoreApi = {
  getMe: async () => {
    const response = await fetch(`${API_BASE_URL}/trust-score/me`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },
};

// ==================== BLOCKCHAIN API ====================
export const blockchainApi = {
  getTransactions: async (limit?: number, offset?: number, type?: string) => {
    const params = new URLSearchParams();
    if (limit != null) params.set('limit', String(limit));
    if (offset != null) params.set('offset', String(offset));
    if (type != null && type.trim()) params.set('type', type.trim());
    const qs = params.toString();
    const url = `${API_BASE_URL}/blockchain/transactions${qs ? `?${qs}` : ''}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    return response.json();
  },
  recordTransaction: async (body: { type?: string; amount?: number; referenceId?: string }) => {
    const response = await fetch(`${API_BASE_URL}/blockchain/transactions`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body || {}),
    });
    return response.json();
  },
  getTransaction: async (transactionId: string) => {
    const response = await fetch(`${API_BASE_URL}/blockchain/transactions/${encodeURIComponent(transactionId)}`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },
  verifyTransaction: async (transactionId: string) => {
    const response = await fetch(`${API_BASE_URL}/blockchain/transactions/${encodeURIComponent(transactionId)}/verify`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },
  getSummary: async (type?: string) => {
    const qs = type != null && type.trim() ? `?type=${encodeURIComponent(type.trim())}` : '';
    const response = await fetch(`${API_BASE_URL}/blockchain/transactions/summary${qs}`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },
  getTransactionTypes: async () => {
    const response = await fetch(`${API_BASE_URL}/blockchain/transactions/types`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },
  getCount: async (type?: string) => {
    const qs = type != null && type.trim() ? `?type=${encodeURIComponent(type.trim())}` : '';
    const response = await fetch(`${API_BASE_URL}/blockchain/transactions/count${qs}`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },
};

// ==================== VAULT API ====================
export interface VaultItem {
  id: string;
  name: string;
  type?: string;
  description?: string | null;
  mediaUrl?: string | null;
  orderId?: string | null;
  manufacturerId?: string | null;
  createdAt?: string;
  updatedAt?: string;
}
export const vaultApi = {
  getItems: async () => {
    const response = await fetch(`${API_BASE_URL}/vault/items`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },
  createItem: async (data: { name: string; type?: string; description?: string; mediaUrl?: string; orderId?: string; manufacturerId?: string }) => {
    const response = await fetch(`${API_BASE_URL}/vault/items`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },
  getItem: async (itemId: string) => {
    const response = await fetch(`${API_BASE_URL}/vault/items/${encodeURIComponent(itemId)}`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },
  updateItem: async (itemId: string, data: { name?: string; type?: string; description?: string; mediaUrl?: string; orderId?: string; manufacturerId?: string }) => {
    const response = await fetch(`${API_BASE_URL}/vault/items/${encodeURIComponent(itemId)}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },
  deleteItem: async (itemId: string) => {
    const response = await fetch(`${API_BASE_URL}/vault/items/${encodeURIComponent(itemId)}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return response.json();
  },
};

// ==================== LOCATION SHARING API ====================
export interface SharedLocation {
  id: string;
  latitude: number;
  longitude: number;
  label?: string | null;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}
export const locationSharingApi = {
  getShared: async () => {
    const response = await fetch(`${API_BASE_URL}/location-sharing/shared`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },
  share: async (data: { latitude: number; longitude: number; label?: string }) => {
    const response = await fetch(`${API_BASE_URL}/location-sharing/share`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },
  stop: async (shareId?: string) => {
    const response = await fetch(`${API_BASE_URL}/location-sharing/stop`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(shareId ? { shareId } : {}),
    });
    return response.json();
  },
  getHistory: async (limit?: number) => {
    const q = limit != null ? `?limit=${limit}` : '';
    const response = await fetch(`${API_BASE_URL}/location-sharing/history${q}`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },
};

// ==================== SHARED LIVING API ====================
export interface SharedLivingGroup {
  id: string;
  name: string;
  description?: string | null;
  createdAt?: string;
  updatedAt?: string;
  bills?: Array<{ id: string; title: string; amount: number; status: string }>;
  chores?: Array<{ id: string; title: string; status: string }>;
  expenses?: Array<{ id: string; title: string; amount: number }>;
}
export const sharedLivingApi = {
  getHouseholds: async () => {
    const response = await fetch(`${API_BASE_URL}/shared-living/households`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },
  getGroups: async () => {
    const response = await fetch(`${API_BASE_URL}/shared-living/groups`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },
  createGroup: async (data: { name?: string; description?: string }) => {
    const response = await fetch(`${API_BASE_URL}/shared-living/groups`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },
  getGroup: async (groupId: string) => {
    const response = await fetch(`${API_BASE_URL}/shared-living/groups/${encodeURIComponent(groupId)}`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },
  addBill: async (groupId: string, data: { title: string; amount: number; dueDate?: string }) => {
    const response = await fetch(`${API_BASE_URL}/shared-living/groups/${encodeURIComponent(groupId)}/bills`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },
  addChore: async (groupId: string, data: { title: string; assignedTo?: string; dueDate?: string }) => {
    const response = await fetch(`${API_BASE_URL}/shared-living/groups/${encodeURIComponent(groupId)}/chores`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },
  addExpense: async (groupId: string, data: { title: string; amount: number; paidBy?: string }) => {
    const response = await fetch(`${API_BASE_URL}/shared-living/groups/${encodeURIComponent(groupId)}/expenses`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },
};

// ==================== GROUP PLANNING API ====================
export interface GroupPlanItineraryItem {
  id: string;
  title: string;
  description?: string | null;
  dateTime?: string | null;
  location?: string | null;
}
export interface GroupPlanWishlistItem {
  id: string;
  title: string;
  url?: string | null;
  notes?: string | null;
}
export interface GroupPlan {
  id: string;
  title: string;
  description?: string | null;
  itinerary?: GroupPlanItineraryItem[];
  wishlist?: GroupPlanWishlistItem[];
  itineraryCount?: number;
  wishlistCount?: number;
}
export const groupPlanningApi = {
  getPlans: async () => {
    const response = await fetch(`${API_BASE_URL}/group-planning/plans`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },
  createPlan: async (data: { title: string }) => {
    const response = await fetch(`${API_BASE_URL}/group-planning/plans`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },
  addItineraryItem: async (planId: string, data: { title: string; description?: string; dateTime?: string; location?: string }) => {
    const response = await fetch(`${API_BASE_URL}/group-planning/plans/${encodeURIComponent(planId)}/itinerary`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },
  addWishlistItem: async (planId: string, data: { title: string; url?: string; notes?: string }) => {
    const response = await fetch(`${API_BASE_URL}/group-planning/plans/${encodeURIComponent(planId)}/wishlist`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },
};

// ==================== HEALTHCARE API ====================
export interface HealthcarePrescription {
  id: string;
  medicationName: string;
  dosage?: string | null;
  refillsRemaining?: number;
  status?: string;
}
export interface HealthcareRecord {
  id: string;
  title: string;
  recordType?: string;
  date?: string | null;
}
export const healthcareApi = {
  getSummary: async () => {
    const response = await fetch(`${API_BASE_URL}/healthcare/summary`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },
  getPrescriptions: async () => {
    const response = await fetch(`${API_BASE_URL}/healthcare/prescriptions`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },
  addPrescription: async (data: { medicationName: string; dosage?: string; refillsRemaining?: number }) => {
    const response = await fetch(`${API_BASE_URL}/healthcare/prescriptions`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },
  getRecords: async () => {
    const response = await fetch(`${API_BASE_URL}/healthcare/records`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },
  addRecord: async (data: { title: string; recordType?: string; notes?: string; date?: string }) => {
    const response = await fetch(`${API_BASE_URL}/healthcare/records`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },
};

// ==================== EDUCATION API ====================
export interface EducationCourse {
  id: string;
  title: string;
  provider?: string | null;
  progressPct?: number;
}
export interface EducationCertification {
  id: string;
  name: string;
  issuer?: string | null;
  issueDate?: string | null;
  expiryDate?: string | null;
}
export const educationApi = {
  getCourses: async () => {
    const response = await fetch(`${API_BASE_URL}/education/courses`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },
  createCourse: async (data: { title: string; provider?: string; progressPct?: number }) => {
    const response = await fetch(`${API_BASE_URL}/education/courses`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },
  getCertifications: async () => {
    const response = await fetch(`${API_BASE_URL}/education/certifications`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },
  createCertification: async (data: { name: string; issuer?: string; issueDate?: string; expiryDate?: string }) => {
    const response = await fetch(`${API_BASE_URL}/education/certifications`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },
};

// ==================== LICENSE MANAGEMENT API ====================
export interface License {
  id: string;
  name: string;
  type?: string | null;
  expiryDate?: string | null;
  status?: string;
}
export const licenseManagementApi = {
  getLicenses: async () => {
    const response = await fetch(`${API_BASE_URL}/license-management/licenses`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },
  createLicense: async (data: { name: string; type?: string; issuer?: string; licenseNumber?: string; expiryDate?: string }) => {
    const response = await fetch(`${API_BASE_URL}/license-management/licenses`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },
  updateLicense: async (licenseId: string, data: { name?: string; type?: string; issuer?: string; licenseNumber?: string; expiryDate?: string }) => {
    const response = await fetch(`${API_BASE_URL}/license-management/licenses/${encodeURIComponent(licenseId)}`, {
      method: 'PUT',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return response.json();
  },
  deleteLicense: async (licenseId: string) => {
    const response = await fetch(`${API_BASE_URL}/license-management/licenses/${encodeURIComponent(licenseId)}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return response.json();
  },
};

// ==================== ART SYSTEM API ====================
export interface ArtSystemArtwork {
  id: string;
  title: string;
  description?: string | null;
  category?: string | null;
  isListed?: boolean;
}
export const artSystemApi = {
  getCreations: async () => {
    const response = await fetch(`${API_BASE_URL}/art-system/creations`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },
  getCollection: async () => {
    const response = await fetch(`${API_BASE_URL}/art-system/collection`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },
  addArtwork: async (data: { title: string; description?: string; mediaUrl?: string; category?: string }) => {
    const response = await fetch(`${API_BASE_URL}/art-system/artworks`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return response.json();
  },
  updateArtwork: async (artworkId: string, data: { title?: string; description?: string; mediaUrl?: string; category?: string }) => {
    const response = await fetch(`${API_BASE_URL}/art-system/artworks/${encodeURIComponent(artworkId)}`, {
      method: 'PUT',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return response.json();
  },
  deleteArtwork: async (artworkId: string) => {
    const response = await fetch(`${API_BASE_URL}/art-system/artworks/${encodeURIComponent(artworkId)}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return response.json();
  },
};

// ==================== GIFT REGISTRY API ====================
export const giftRegistryApi = {
  list: async () => {
    const response = await fetch(`${API_BASE_URL}/gift-registry`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },
  get: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/gift-registry/${encodeURIComponent(id)}`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },
  create: async (data: { eventType: string; eventDate?: string; title: string; description?: string; isActive?: boolean; items?: Array<{ name: string; quantity?: number; notes?: string; purchased?: boolean }> }) => {
    const response = await fetch(`${API_BASE_URL}/gift-registry`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },
  update: async (id: string, data: { eventType?: string; eventDate?: string; title?: string; description?: string; isActive?: boolean }) => {
    const response = await fetch(`${API_BASE_URL}/gift-registry/${encodeURIComponent(id)}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },
  delete: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/gift-registry/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return response.json();
  },
};

// ==================== PERSONAL INVENTORY API ====================
export interface PersonalInventoryItem {
  id: string;
  name: string;
  category?: string | null;
  description?: string | null;
  notes?: string | null;
  location?: string | null;
  warrantyExpiresAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}
export const personalInventoryApi = {
  getItems: async () => {
    const response = await fetch(`${API_BASE_URL}/personal-inventory/items`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },
  createItem: async (data: { name: string; category?: string; description?: string; notes?: string; location?: string; warrantyExpiresAt?: string }) => {
    const response = await fetch(`${API_BASE_URL}/personal-inventory/items`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return response.json();
  },
  updateItem: async (id: string, data: { name?: string; category?: string; description?: string; notes?: string; location?: string; warrantyExpiresAt?: string }) => {
    const response = await fetch(`${API_BASE_URL}/personal-inventory/items/${id}`, {
      method: 'PUT',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return response.json();
  },
  deleteItem: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/personal-inventory/items/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return response.json();
  },
};

// ==================== PRODUCTS PUBLIC API (Search / Marketplace) ====================
export const productsPublicApi = {
  list: async (params?: { search?: string; category?: string; page?: number; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.search) q.set('search', params.search);
    if (params?.category) q.set('category', params.category);
    if (params?.page) q.set('page', String(params.page ?? 1));
    if (params?.limit) q.set('limit', String(params.limit ?? 24));
    const url = `${API_BASE_URL}/products/public${q.toString() ? `?${q.toString()}` : ''}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    return response.json();
  },
};

// ==================== BUSINESS PUBLIC API (Discover Businesses) ====================
export const businessPublicApi = {
  list: async (params?: { limit?: number; offset?: number; search?: string }) => {
    const q = new URLSearchParams();
    if (params?.limit != null) q.set('limit', String(params.limit));
    if (params?.offset != null) q.set('offset', String(params.offset));
    if (params?.search) q.set('search', params.search);
    const url = `${API_BASE_URL}/business/public${q.toString() ? `?${q.toString()}` : ''}`;
    const response = await fetch(url);
    return response.json();
  },
};

// ==================== PERSONAL MARKETPLACE (C2C Listings) API ====================
export const personalMarketplaceApi = {
  listMine: async () => {
    const response = await fetch(`${API_BASE_URL}/personal-marketplace/listings`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },
  create: async (data: {
    title: string;
    description?: string;
    price: number;
    images: string[];
    category?: string;
    condition?: string;
    location?: string;
    acceptMangoCredits?: boolean;
    shippingAvailable?: boolean;
    pickupOnly?: boolean;
  }) => {
    const response = await fetch(`${API_BASE_URL}/personal-marketplace/listings`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },
  get: async (listingId: string) => {
    const response = await fetch(`${API_BASE_URL}/personal-marketplace/listings/${encodeURIComponent(listingId)}`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },
  update: async (listingId: string, data: Partial<{
    title: string;
    description: string;
    price: number;
    images: string[];
    category: string;
    condition: string;
    location: string;
    status: string;
    acceptMangoCredits: boolean;
    shippingAvailable: boolean;
    pickupOnly: boolean;
  }>) => {
    const response = await fetch(`${API_BASE_URL}/personal-marketplace/listings/${encodeURIComponent(listingId)}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },
  delete: async (listingId: string) => {
    const response = await fetch(`${API_BASE_URL}/personal-marketplace/listings/${encodeURIComponent(listingId)}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return response.json();
  },
  listPublic: async (params?: { limit?: number; offset?: number; category?: string; search?: string }) => {
    const q = new URLSearchParams();
    if (params?.limit != null) q.set('limit', String(params.limit));
    if (params?.offset != null) q.set('offset', String(params.offset));
    if (params?.category) q.set('category', params.category);
    if (params?.search) q.set('search', params.search);
    const url = `${API_BASE_URL}/personal-marketplace/public${q.toString() ? `?${q.toString()}` : ''}`;
    const response = await fetch(url);
    return response.json();
  },
};

// ==================== MARKETPLACE / UNIVERSAL SEARCH API ====================
export const marketplaceApi = {
  // Get dynamic filters for a category
  getFilters: async (categorySlug: string) => {
    const response = await fetch(
      `${API_BASE_URL}/marketplace/filters/${categorySlug}`,
      { headers: getAuthHeaders() }
    );
    return response.json();
  },

  // Get all categories with hierarchy
  getCategories: async () => {
    const response = await fetch(`${API_BASE_URL}/marketplace/categories`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  // Universal search - products, businesses, services
  search: async (filters: SearchFilters) => {
    const response = await fetch(`${API_BASE_URL}/marketplace/search`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(filters),
    });
    return response.json();
  },

  // Get product/listing details
  getListing: async (listingId: string) => {
    const response = await fetch(
      `${API_BASE_URL}/marketplace/listings/${listingId}`,
      { headers: getAuthHeaders() }
    );
    return response.json();
  },

  // Get business profile
  getBusiness: async (businessId: string) => {
    const response = await fetch(
      `${API_BASE_URL}/marketplace/businesses/${businessId}`,
      { headers: getAuthHeaders() }
    );
    return response.json();
  },

  // Add to wishlist
  addToWishlist: async (listingId: string) => {
    const response = await fetch(`${API_BASE_URL}/marketplace/wishlist`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ listingId }),
    });
    return response.json();
  },

  // Get featured/trending
  getFeatured: async () => {
    const response = await fetch(`${API_BASE_URL}/marketplace/featured`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  // Search listings (alias for search - used by Marketplace page)
  searchListings: async (filters: any) => {
    const response = await fetch(`${API_BASE_URL}/marketplace/search`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(filters),
    });
    return response.json();
  },

  // Save a marketplace listing to the personal wishlist (POST /wishlist). Shop products
  // (ids prefixed "p-") keep their productId; personal listings are saved by name/image/price.
  saveListing: async (listing: { id: string; title: string; price?: number; imageUrl?: string }) => {
    const productId = listing.id.startsWith('p-') ? listing.id.slice(2) : undefined;
    const response = await fetch(`${API_BASE_URL}/wishlist`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        productId,
        name: listing.title || 'Marketplace item',
        imageUrl: listing.imageUrl,
        priceAtSave: listing.price,
        note: productId ? undefined : `Marketplace listing ${listing.id}`,
      }),
    });
    return response.json();
  },
};

// ==================== B2B MARKETPLACE API ====================
export const b2bApi = {
  // Search for suppliers, manufacturers, partners
  searchBusinesses: async (filters: {
    query?: string;
    category?: string;
    businessType?: 'manufacturer' | 'supplier' | 'wholesaler' | 'service_provider' | 'retailer';
    capabilities?: string[];
    location?: { lat: number; lng: number; radiusKm: number };
    moq?: number; // Minimum Order Quantity
    certifications?: string[];
    page?: number;
    perPage?: number;
  }) => {
    const response = await fetch(`${API_BASE_URL}/b2b/search`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(filters),
    });
    return response.json();
  },

  // Send RFQ (Request for Quote)
  sendRFQ: async (data: {
    toBusinessId: string;
    productDescription: string;
    quantity: number;
    specifications?: Record<string, any>;
    deadline?: string;
    attachments?: string[];
  }) => {
    const response = await fetch(`${API_BASE_URL}/b2b/rfq`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },

  // Get my RFQs (sent/received)
  getRFQs: async (type: 'sent' | 'received', page = 1) => {
    const response = await fetch(
      `${API_BASE_URL}/b2b/rfq?type=${type}&page=${page}`,
      { headers: getAuthHeaders() }
    );
    return response.json();
  },

  // Respond to RFQ
  respondToRFQ: async (rfqId: string, data: {
    quotedPrice: number;
    leadTime: string;
    notes?: string;
    validUntil?: string;
  }) => {
    const response = await fetch(`${API_BASE_URL}/b2b/rfq/${rfqId}/respond`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },

  // Get business capabilities/catalog
  getBusinessCatalog: async (businessId: string) => {
    const response = await fetch(
      `${API_BASE_URL}/b2b/businesses/${businessId}/catalog`,
      { headers: getAuthHeaders() }
    );
    return response.json();
  },

  // Post a project/need for businesses to bid on
  postProject: async (data: {
    title: string;
    description: string;
    category: string;
    requirements: string[];
    budget?: { min: number; max: number };
    deadline?: string;
    attachments?: string[];
  }) => {
    const response = await fetch(`${API_BASE_URL}/b2b/projects`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },

  // Browse open projects
  getProjects: async (filters?: {
    category?: string;
    minBudget?: number;
    maxBudget?: number;
    page?: number;
  }) => {
    const params = new URLSearchParams(filters as any);
    const response = await fetch(`${API_BASE_URL}/b2b/projects?${params}`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  // Bid on a project
  bidOnProject: async (projectId: string, data: {
    proposedPrice: number;
    timeline: string;
    proposal: string;
  }) => {
    const response = await fetch(`${API_BASE_URL}/b2b/projects/${projectId}/bid`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },
};

// ==================== MESSAGES API ====================
export const messagesApi = {
  getConversations: async (page = 1) => {
    const response = await fetch(
      `${API_BASE_URL}/messages/conversations?page=${page}`,
      { headers: getAuthHeaders() }
    );
    return response.json();
  },

  getMessages: async (conversationId: string, page = 1) => {
    const response = await fetch(
      `${API_BASE_URL}/messages/${conversationId}?page=${page}`,
      { headers: getAuthHeaders() }
    );
    return response.json();
  },

  sendMessage: async (conversationId: string, content: string, attachments?: string[]) => {
    const response = await fetch(`${API_BASE_URL}/messages/${conversationId}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ content, attachments }),
    });
    return response.json();
  },

  startConversation: async (recipientId: string, message: string) => {
    const response = await fetch(`${API_BASE_URL}/messages/new`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ recipientId, message }),
    });
    return response.json();
  },

  // Custom emojis
  getMyEmojis: async () => {
    const response = await fetch(`${API_BASE_URL}/messages/emojis/mine`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  getAvailableEmojis: async () => {
    const response = await fetch(`${API_BASE_URL}/messages/emojis/available`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  createEmoji: async (data: { name: string; imageData: string }) => {
    const response = await fetch(`${API_BASE_URL}/messages/emojis`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },

  // Mark conversation as read
  markAsRead: async (conversationId: string) => {
    const response = await fetch(`${API_BASE_URL}/messages/${conversationId}/read`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return response.json();
  },
};

// ==================== PERSONAL PUSH API ====================
export const personalPushApi = {
  getVapidPublicKey: async () => {
    const response = await fetch(`${API_BASE_URL}/push/vapid-public-key`);
    return response.json();
  },

  subscribe: async (subscription: PushSubscriptionJSON) => {
    const response = await fetch(`${API_BASE_URL}/personal-push/subscribe`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ subscription }),
    });
    return response.json();
  },

  unsubscribe: async (endpoint: string) => {
    const response = await fetch(`${API_BASE_URL}/personal-push/unsubscribe`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ endpoint }),
    });
    return response.json();
  },
};

// ==================== GROUPS API ====================
export const groupsApi = {
  getMyGroups: async () => {
    const response = await fetch(`${API_BASE_URL}/groups/mine`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  discoverGroups: async (filters?: { category?: string; query?: string }) => {
    const params = new URLSearchParams(filters as any);
    const response = await fetch(`${API_BASE_URL}/groups/discover?${params}`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  createGroup: async (data: {
    name: string;
    description: string;
    category: string;
    isPublic: boolean;
    imageUrl?: string;
    type?: 'personal' | 'community';
    membershipFee?: number;
    membershipPeriod?: 'weekly' | 'yearly';
  }) => {
    const response = await fetch(`${API_BASE_URL}/groups`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },

  inviteAdmin: async (groupId: string, email: string) => {
    const response = await fetch(`${API_BASE_URL}/groups/${groupId}/invite-admin`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ email }),
    });
    return response.json();
  },

  createInviteLink: async (groupId: string) => {
    const response = await fetch(`${API_BASE_URL}/groups/${groupId}/invite-link`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  joinByInvite: async (token: string) => {
    const response = await fetch(`${API_BASE_URL}/groups/join-by-invite`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ token }),
    });
    return response.json();
  },

  joinGroup: async (groupId: string) => {
    const response = await fetch(`${API_BASE_URL}/groups/${groupId}/join`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  leaveGroup: async (groupId: string) => {
    const response = await fetch(`${API_BASE_URL}/groups/${groupId}/leave`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  getGroupMessages: async (groupId: string, page = 1, channelId?: string, category?: string) => {
    const params = new URLSearchParams({ page: String(page) });
    if (channelId) params.set('channelId', channelId);
    if (category) params.set('category', category);
    const response = await fetch(
      `${API_BASE_URL}/groups/${groupId}/messages?${params.toString()}`,
      { headers: getAuthHeaders() }
    );
    return response.json();
  },

  sendGroupMessage: async (
    groupId: string,
    content: string,
    options?: {
      channelId?: string;
      attachments?: string[] | { type: 'image' | 'file'; url: string; name?: string }[];
      category?: string;
      linkedProductId?: string;
      type?: string;
    }
  ) => {
    const body: {
      content: string;
      channelId?: string;
      attachments?: string[] | { type: 'image' | 'file'; url: string; name?: string }[];
      category?: string;
      linkedProductId?: string;
      type?: string;
    } = { content };
    if (options?.channelId) body.channelId = options.channelId;
    if (options?.attachments?.length) body.attachments = options.attachments;
    if (options?.category) body.category = options.category;
    if (options?.linkedProductId) body.linkedProductId = options.linkedProductId;
    if (options?.type) body.type = options.type;
    const response = await fetch(`${API_BASE_URL}/groups/${groupId}/messages`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });
    return response.json();
  },

  /** Self-service per-group preference for how event photos get shared (manual | prompt | auto). */
  setPhotoPostingMode: async (groupId: string, photoPostingMode: 'manual' | 'prompt' | 'auto') => {
    const response = await fetch(`${API_BASE_URL}/groups/${groupId}/members/me/photo-posting-mode`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ photoPostingMode }),
    });
    return response.json();
  },

  /** Currently-open 'auto' event windows — powers the Android native background watcher. */
  getActiveAutoWindows: async () => {
    const response = await fetch(`${API_BASE_URL}/groups/events/active-auto-windows`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  /** Time window (+ location, chat channel) to match candidate event photos against. */
  getEventPhotoWindow: async (groupId: string, eventId: string) => {
    const response = await fetch(`${API_BASE_URL}/groups/${groupId}/events/${eventId}/photo-window`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  /** Recent events awaiting a "share your photos?" prompt (photoPostingMode 'prompt'/'auto'). */
  getMyRecentEventsForPhotoPrompt: async () => {
    const response = await fetch(`${API_BASE_URL}/groups/events/mine`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  dismissEventPhotoPrompt: async (eventId: string) => {
    const response = await fetch(`${API_BASE_URL}/groups/events/${eventId}/photo-prompt-dismiss`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  eventRsvp: async (groupId: string, messageId: string) => {
    const response = await fetch(`${API_BASE_URL}/groups/${groupId}/messages/${messageId}/event/rsvp`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  eventUnrsvp: async (groupId: string, messageId: string) => {
    const response = await fetch(`${API_BASE_URL}/groups/${groupId}/messages/${messageId}/event/unrsvp`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  getGroupProducts: async (groupId: string, page = 1, status = 'active') => {
    const params = new URLSearchParams({ page: String(page), status });
    const response = await fetch(
      `${API_BASE_URL}/groups/${groupId}/products?${params}`,
      { headers: getAuthHeaders() }
    );
    return response.json();
  },

  getGroupProduct: async (groupId: string, productId: string) => {
    const response = await fetch(
      `${API_BASE_URL}/groups/${groupId}/products/${productId}`,
      { headers: getAuthHeaders() }
    );
    return response.json();
  },

  createGroupProduct: async (groupId: string, data: { title: string; description?: string; price?: number; imageUrls?: string[]; externalUrl?: string }) => {
    const response = await fetch(`${API_BASE_URL}/groups/${groupId}/products`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },

  createGroupChannel: async (groupId: string, data: {
    name: string;
    description?: string;
    type?: string;
    isPrivate?: boolean;
    icon?: string;
    allowedRoles?: string[];
  }) => {
    const response = await fetch(`${API_BASE_URL}/groups/${groupId}/channels`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },

  createGroupEvent: async (groupId: string, data: {
    title: string;
    description?: string;
    date?: string;
    time?: string;
    location?: string;
    maxAttendees?: number;
    createChat?: boolean;
    /** Minutes after the event starts before attendees get asked to share photos. Default 60. */
    photoPromptDelayMinutes?: number;
  }) => {
    const response = await fetch(`${API_BASE_URL}/groups/${groupId}/events`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },

  /** Per-attendee override of the event's default photo-prompt delay. Pass null to clear it. */
  setEventPhotoPromptDelay: async (eventId: string, delayMinutes: number | null) => {
    const response = await fetch(`${API_BASE_URL}/groups/events/${eventId}/photo-prompt-delay`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ delayMinutes }),
    });
    return response.json();
  },

  uploadToGroup: async (groupId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${API_BASE_URL}/groups/${groupId}/upload`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getToken()}` },
      body: formData,
    });
    return response.json();
  },

  getGroupSettings: async (groupId: string) => {
    const response = await fetch(`${API_BASE_URL}/groups/${groupId}/settings`, { headers: getAuthHeaders() });
    return response.json();
  },

  updateGroupSettings: async (groupId: string, data: {
    cronSecret?: string | null;
    aiOrganizingEnabled?: boolean;
    addAiCredits?: number;
    aiChatEnabled?: boolean;
    aiChatBillingMode?: 'requester' | 'split_immediate' | 'group' | 'split_daily';
    translationBillingMode?: 'requester' | 'split_immediate' | 'group' | 'split_daily';
  }) => {
    const response = await fetch(`${API_BASE_URL}/groups/${groupId}/settings`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },

  recordMembershipPayment: async (
    groupId: string,
    data: { userId: string; amount: number; periodStart: string; periodEnd: string }
  ) => {
    const response = await fetch(`${API_BASE_URL}/groups/${groupId}/membership-payments`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },

  createMembershipPaymentIntent: async (groupId: string, data?: { amount?: number; periodStart?: string; periodEnd?: string }) => {
    const response = await fetch(`${API_BASE_URL}/groups/${groupId}/payment-intent/membership`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data || {}),
    });
    return response.json();
  },

  /** Create Stripe Checkout for joining a paid group (pay first, then join via webhook). Returns { url }. */
  createJoinCheckout: async (groupId: string) => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const payload = origin ? { baseUrl: origin } : {};
    const response = await fetch(`${API_BASE_URL}/groups/${groupId}/checkout/join`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    return response.json();
  },

  /** Create Stripe Checkout Session for membership fee. Returns { url } to redirect user. */
  createMembershipCheckout: async (groupId: string, data?: { amount?: number; periodStart?: string; periodEnd?: string }) => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const payload = { ...(data || {}) } as { amount?: number; periodStart?: string; periodEnd?: string; baseUrl?: string };
    if (origin) payload.baseUrl = origin;
    const response = await fetch(`${API_BASE_URL}/groups/${groupId}/checkout/membership`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    return response.json();
  },

  createAiCreditsPaymentIntent: async (groupId: string, data?: { tierId?: string; credits?: number; amountInCents?: number }) => {
    const response = await fetch(`${API_BASE_URL}/groups/${groupId}/payment-intent/ai-credits`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data || {}),
    });
    return response.json();
  },

  /** Create Stripe Checkout Session for AI credits. Returns { url } to redirect user. */
  createAiCreditsCheckout: async (groupId: string, data?: { tierId?: string; credits?: number }) => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const payload = { ...(data || {}) } as { tierId?: string; credits?: number; baseUrl?: string };
    if (origin) payload.baseUrl = origin;
    const response = await fetch(`${API_BASE_URL}/groups/${groupId}/checkout/ai-credits`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    return response.json();
  },

  getAiCreditsTiers: async () => {
    const response = await fetch(`${API_BASE_URL}/groups/ai-credits-tiers`, { headers: getAuthHeaders() });
    return response.json();
  },

  getMembershipInfo: async (groupId: string) => {
    const response = await fetch(`${API_BASE_URL}/groups/${groupId}/membership-info`, { headers: getAuthHeaders() });
    return response.json();
  },

  getGroupWallet: async (groupId: string) => {
    const response = await fetch(`${API_BASE_URL}/groups/${groupId}/wallet`, { headers: getAuthHeaders() });
    return response.json();
  },

  createPoll: async (groupId: string, data: { question: string; options: string[]; type?: 'informational' | 'binding'; expiresAt?: string }) => {
    const response = await fetch(`${API_BASE_URL}/groups/${groupId}/polls`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },

  getPolls: async (groupId: string) => {
    const response = await fetch(`${API_BASE_URL}/groups/${groupId}/polls`, { headers: getAuthHeaders() });
    return response.json();
  },

  getGroupAuditLog: async (groupId: string) => {
    const response = await fetch(`${API_BASE_URL}/groups/${groupId}/audit-log`, { headers: getAuthHeaders() });
    return response.json();
  },

  getGroupMembers: async (groupId: string) => {
    const response = await fetch(`${API_BASE_URL}/groups/${groupId}/members`, { headers: getAuthHeaders() });
    return response.json();
  },

  updateMemberRole: async (groupId: string, targetUserId: string, role: 'admin' | 'moderator' | 'member') => {
    const response = await fetch(`${API_BASE_URL}/groups/${groupId}/members/${targetUserId}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ role }),
    });
    return response.json();
  },

  votePoll: async (groupId: string, pollId: string, optionIndex: number) => {
    const response = await fetch(`${API_BASE_URL}/groups/${groupId}/polls/${pollId}/vote`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ optionIndex }),
    });
    return response.json();
  },
};

// ==================== GROUP HIERARCHY API (Phase 1) ====================
// Additive endpoints alongside groupsApi above, for the arbitrary-depth group tree
// (Van Life > Country > Region > City style nesting). See group-hierarchy.routes.ts.
export const groupHierarchyApi = {
  getMyTree: async () => {
    const response = await fetch(`${API_BASE_URL}/groups/tree/mine`, { headers: getAuthHeaders() });
    return response.json();
  },

  getSubtree: async (groupId: string, maxDepth?: number) => {
    const params = maxDepth !== undefined ? `?maxDepth=${maxDepth}` : '';
    const response = await fetch(`${API_BASE_URL}/groups/${groupId}/tree${params}`, { headers: getAuthHeaders() });
    return response.json();
  },

  getAncestors: async (groupId: string) => {
    const response = await fetch(`${API_BASE_URL}/groups/${groupId}/ancestors`, { headers: getAuthHeaders() });
    return response.json();
  },

  getFeed: async (groupId: string, scope: 'node' | 'subtree' = 'node', options?: { before?: string; limit?: number }) => {
    const params = new URLSearchParams({ scope });
    if (options?.before) params.set('before', options.before);
    if (options?.limit) params.set('limit', String(options.limit));
    const response = await fetch(`${API_BASE_URL}/groups/${groupId}/feed?${params.toString()}`, { headers: getAuthHeaders() });
    return response.json();
  },

  createChild: async (
    groupId: string,
    data: { name: string; description?: string; nodeKind?: 'space' | 'topic'; category?: string; isPublic?: boolean; imageUrl?: string }
  ) => {
    const response = await fetch(`${API_BASE_URL}/groups/${groupId}/children`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },

  updateVisibility: async (
    groupId: string,
    data: { contentRollsUp?: boolean; grantsAncestorRead?: boolean; postPolicy?: 'members_only' | 'ancestor_members' }
  ) => {
    const response = await fetch(`${API_BASE_URL}/groups/${groupId}/visibility`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },

  getAdminChannelMessages: async (groupId: string) => {
    const response = await fetch(`${API_BASE_URL}/groups/${groupId}/admin-channel/messages`, { headers: getAuthHeaders() });
    return response.json();
  },

  sendAdminChannelMessage: async (groupId: string, content: string) => {
    const response = await fetch(`${API_BASE_URL}/groups/${groupId}/admin-channel/messages`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ content }),
    });
    return response.json();
  },

  moveNode: async (groupId: string, newParentId: string) => {
    const response = await fetch(`${API_BASE_URL}/groups/${groupId}/move`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ newParentId }),
    });
    return response.json();
  },

  promoteChannel: async (groupId: string, channelId: string, nodeKind?: 'space' | 'topic') => {
    const response = await fetch(`${API_BASE_URL}/groups/${groupId}/channels/${channelId}/promote`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ nodeKind }),
    });
    return response.json();
  },
};

// ==================== JOBS API ====================
export const jobsApi = {
  // Job seeker endpoints
  searchJobs: async (filters?: {
    query?: string;
    category?: string;
    type?: 'full-time' | 'part-time' | 'contract' | 'casual';
    minSalary?: number;
    location?: { lat: number; lng: number; radiusKm: number };
    remote?: boolean;
    page?: number;
  }) => {
    const response = await fetch(`${API_BASE_URL}/jobs/search`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(filters),
    });
    return response.json();
  },

  getJobDetails: async (jobId: string) => {
    const response = await fetch(`${API_BASE_URL}/jobs/${jobId}`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  applyForJob: async (jobId: string, data: {
    coverLetter?: string;
    resumeUrl?: string;
    answers?: Record<string, string>;
  }) => {
    const response = await fetch(`${API_BASE_URL}/jobs/${jobId}/apply`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },

  getMyApplications: async (page = 1) => {
    const response = await fetch(
      `${API_BASE_URL}/jobs/applications?page=${page}`,
      { headers: getAuthHeaders() }
    );
    return response.json();
  },

  // Job profile
  getJobProfile: async () => {
    const response = await fetch(`${API_BASE_URL}/jobs/profile`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  updateJobProfile: async (data: {
    headline?: string;
    summary?: string;
    skills?: string[];
    experience?: any[];
    education?: any[];
    certifications?: any[];
    openToWork?: boolean;
    preferredJobTypes?: string[];
    preferredLocations?: string[];
    expectedSalary?: { min: number; max: number };
  }) => {
    const response = await fetch(`${API_BASE_URL}/jobs/profile`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },

  setOpenToWork: async (isOpen: boolean) => {
    const response = await fetch(`${API_BASE_URL}/jobs/profile/open-to-work`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ openToWork: isOpen }),
    });
    return response.json();
  },

  // Aliases for Jobs page compatibility
  getMyProfile: async () => {
    const response = await fetch(`${API_BASE_URL}/jobs/profile`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  updateProfile: async (data: any) => {
    const response = await fetch(`${API_BASE_URL}/jobs/profile`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },

  applyToJob: async (jobId: string, coverLetter?: string) => {
    const response = await fetch(`${API_BASE_URL}/jobs/${jobId}/apply`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ coverLetter }),
    });
    return response.json();
  },
};

// ==================== CONNECTIONS API ====================
export const connectionsApi = {
  getConnections: async (page = 1) => {
    const response = await fetch(
      `${API_BASE_URL}/connections?page=${page}`,
      { headers: getAuthHeaders() }
    );
    return response.json();
  },

  getPendingRequests: async () => {
    const response = await fetch(`${API_BASE_URL}/connections/pending`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  sendRequest: async (userId: string, message?: string) => {
    const response = await fetch(`${API_BASE_URL}/connections/request`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ userId, message }),
    });
    return response.json();
  },

  acceptRequest: async (requestId: string) => {
    const response = await fetch(`${API_BASE_URL}/connections/${requestId}/accept`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  rejectRequest: async (requestId: string) => {
    const response = await fetch(`${API_BASE_URL}/connections/${requestId}/reject`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  searchPeople: async (query: string, page = 1) => {
    const response = await fetch(
      `${API_BASE_URL}/connections/search?q=${encodeURIComponent(query)}&page=${page}`,
      { headers: getAuthHeaders() }
    );
    return response.json();
  },

  // Alias for Connections page compatibility
  searchUsers: async (query: string, page = 1) => {
    const response = await fetch(
      `${API_BASE_URL}/connections/search?q=${encodeURIComponent(query)}&page=${page}`,
      { headers: getAuthHeaders() }
    );
    return response.json();
  },
};

// ==================== ORDERS API ====================
export const ordersApi = {
  getOrders: async (page = 1, status?: string) => {
    const params = new URLSearchParams({ page: String(page) });
    if (status) params.append('status', status);
    const response = await fetch(`${API_BASE_URL}/orders?${params}`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  getOrderDetails: async (orderId: string) => {
    const response = await fetch(`${API_BASE_URL}/orders/${orderId}`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  trackOrder: async (orderId: string) => {
    const response = await fetch(`${API_BASE_URL}/orders/${orderId}/tracking`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  // Alias for Dashboard page compatibility
  getMyOrders: async (page = 1, limit = 10) => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    const response = await fetch(`${API_BASE_URL}/orders?${params}`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },
};

// ==================== TICKETS API ====================
export const ticketsApi = {
  getTickets: async (upcoming = true) => {
    const params = upcoming ? '?upcoming=true' : '';
    const response = await fetch(
      `${API_BASE_URL}/tickets/my${params}`,
      { headers: getAuthHeaders() }
    );
    return response.json();
  },

  getTicketDetails: async (ticketId: string) => {
    const response = await fetch(`${API_BASE_URL}/tickets/${ticketId}`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  transferTicket: async (ticketId: string, recipientId: string) => {
    const response = await fetch(`${API_BASE_URL}/tickets/${ticketId}/transfer`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ recipientId }),
    });
    return response.json();
  },

  // Alias for Dashboard page compatibility
  getMyTickets: async () => {
    const response = await fetch(`${API_BASE_URL}/tickets/my?upcoming=true`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  getWaitingList: async () => {
    const response = await fetch(`${API_BASE_URL}/waiting-list/my`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  joinWaitingList: async (eventId: string) => {
    const response = await fetch(`${API_BASE_URL}/events/${encodeURIComponent(eventId)}/waiting-list`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return response.json();
  },
};

// ==================== EVENTS API ====================
export const eventsApi = {
  /** List public/upcoming events (uses /events/public - no auth required for discovery) */
  getUpcoming: async (page = 1, filters?: { category?: string; businessId?: string }) => {
    const params = new URLSearchParams({ page: String(page), limit: '20' });
    if (filters?.businessId) params.set('businessId', filters.businessId);
    const response = await fetch(`${API_BASE_URL}/events/public?${params}`);
    const json = await response.json();
    // Normalize: backend returns { events, pagination } not { success, data }
    if (json.events) {
      return { success: true, data: json.events, pagination: json.pagination };
    }
    return { success: false, error: json.error || 'Failed to load events', data: [] };
  },

  getEventDetails: async (eventId: string) => {
    const response = await fetch(`${API_BASE_URL}/events/public/${eventId}`);
    const json = await response.json();
    if (json.event) return { success: true, data: json.event };
    return { success: false, error: json.error || 'Event not found', data: null };
  },

  registerInterest: async (eventId: string) => {
    const response = await fetch(`${API_BASE_URL}/events/${eventId}/interest`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  purchaseTickets: async (eventId: string, tickets: { tierId: string; quantity: number }[]) => {
    const response = await fetch(`${API_BASE_URL}/events/${eventId}/purchase`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ tickets }),
    });
    return response.json();
  },
};

// ==================== SUBSCRIPTIONS API ====================
export const subscriptionsApi = {
  getMySubscriptions: async () => {
    const response = await fetch(`${API_BASE_URL}/subscriptions/mine`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  subscribeToBusiness: async (businessId: string, planName: string, options?: { amount?: number; billingCycle?: string; cancelAfterMonths?: number }) => {
    const response = await fetch(`${API_BASE_URL}/subscriptions/subscribe`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ businessId, planName, ...options }),
    });
    return response.json();
  },

  setCancelAfter: async (subscriptionId: string, cancelAfterMonths: number | null) => {
    const response = await fetch(`${API_BASE_URL}/subscriptions/${subscriptionId}/set-cancel-after`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ cancelAfterMonths }),
    });
    return response.json();
  },

  cancelSubscription: async (subscriptionId: string, cancelAtEndOfPeriod?: boolean) => {
    const response = await fetch(`${API_BASE_URL}/subscriptions/${subscriptionId}/cancel`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ cancelAtEndOfPeriod }),
    });
    return response.json();
  },

  pauseSubscription: async (subscriptionId: string, options?: { resumeAt?: string }) => {
    const response = await fetch(`${API_BASE_URL}/subscriptions/${subscriptionId}/pause`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(options || {}),
    });
    return response.json();
  },

  reactivateSubscription: async (subscriptionId: string) => {
    const response = await fetch(`${API_BASE_URL}/subscriptions/${subscriptionId}/reactivate`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  // Platform subscription (Free/Social/Pro/Premium)
  upgradePlan: async (planId: string, paymentMethodId: string) => {
    const response = await fetch(`${API_BASE_URL}/subscriptions/platform/upgrade`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ planId, paymentMethodId }),
    });
    return response.json();
  },

  downgradePlan: async (planId: string) => {
    const response = await fetch(`${API_BASE_URL}/subscriptions/platform/downgrade`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ planId }),
    });
    return response.json();
  },
};

// ==================== FILE UPLOAD API ====================
export const uploadApi = {
  // Proxy upload to object storage (POST /storage/upload-attachment). Returns { success, fileId, url, key }.
  // `type` is kept for callers; storage files every personal upload as an ATTACHMENT.
  uploadFile: async (file: File, _type: 'image' | 'document' | 'video') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('fileType', 'ATTACHMENT');
    const response = await fetch(`${API_BASE_URL}/storage/upload-attachment`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getToken()}` },
      body: formData,
    });
    return response.json();
  },

  uploadMultiple: async (files: File[], type: 'image' | 'document' | 'video') => {
    const formData = new FormData();
    files.forEach((file, i) => formData.append(`files[${i}]`, file));
    formData.append('type', type);
    const response = await fetch(`${API_BASE_URL}/upload/multiple`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getToken()}` },
      body: formData,
    });
    return response.json();
  },
};

// ==================== EVENT CHAT API ====================
// Every call here takes an explicit Customer session token (see utils/customerSession.ts) —
// event-chat's backend access check requires a Customer-type JWT (ticket holder), which is a
// different auth system than the Mango Personal account token getAuthHeaders() uses elsewhere
// in this file. The token is exchanged on-demand from the caller's PersonalAccount session via
// POST /customer-auth/session-from-personal (see customerBridgeApi below).
export const eventChatApi = {
  checkAccess: async (token: string, eventId: string) => {
    const response = await fetch(`${API_BASE_URL}/event-chat/${eventId}/access`, {
      headers: authHeadersForToken(token),
    });
    return response.json();
  },

  getMessages: async (token: string, eventId: string, page = 1, limit = 50) => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    const response = await fetch(`${API_BASE_URL}/event-chat/${eventId}/messages?${params}`, {
      headers: authHeadersForToken(token),
    });
    return response.json();
  },

  sendMessage: async (
    token: string,
    eventId: string,
    data: { content?: string; replyToId?: string; topicId?: string; photoUrl?: string }
  ) => {
    const response = await fetch(`${API_BASE_URL}/event-chat/${eventId}/messages`, {
      method: 'POST',
      headers: authHeadersForToken(token),
      body: JSON.stringify(data),
    });
    return response.json();
  },

  deleteMessage: async (token: string, eventId: string, messageId: string) => {
    const response = await fetch(`${API_BASE_URL}/event-chat/${eventId}/messages/${messageId}`, {
      method: 'DELETE',
      headers: authHeadersForToken(token),
    });
    return response.json();
  },

  saveMessage: async (token: string, eventId: string, messageId: string) => {
    const response = await fetch(`${API_BASE_URL}/event-chat/${eventId}/messages/${messageId}/save`, {
      method: 'POST',
      headers: authHeadersForToken(token),
    });
    return response.json();
  },

  unsaveMessage: async (token: string, eventId: string, messageId: string) => {
    const response = await fetch(`${API_BASE_URL}/event-chat/${eventId}/messages/${messageId}/save`, {
      method: 'DELETE',
      headers: authHeadersForToken(token),
    });
    return response.json();
  },

  getParticipants: async (token: string, eventId: string) => {
    const response = await fetch(`${API_BASE_URL}/event-chat/${eventId}/participants`, {
      headers: authHeadersForToken(token),
    });
    return response.json();
  },
};

// ==================== CUSTOMER SESSION BRIDGE ====================
// Exchanges the Mango Personal account session for a business-scoped Customer session, so a
// Social Circle user can post in a ticketed event's chat without a separate shop login.
export const customerBridgeApi = {
  sessionFromPersonal: async (businessId: string) => {
    const response = await fetch(`${API_BASE_URL}/customer-auth/session-from-personal`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ businessId }),
    });
    return response.json();
  },
};

// ==================== WORKPLACES API ====================
// For staff accessing businesses they work at
export const workplacesApi = {
  // Get all workplaces where user is staff
  getMyWorkplaces: async () => {
    const response = await fetch(`${API_BASE_URL}/workplaces`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  // Get pending invitations
  getPendingInvitations: async () => {
    const response = await fetch(`${API_BASE_URL}/workplaces/invitations`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  // Get work history (past jobs)
  getWorkHistory: async () => {
    const response = await fetch(`${API_BASE_URL}/workplaces/history`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  // Accept invitation
  acceptInvitation: async (invitationId: string) => {
    const response = await fetch(`${API_BASE_URL}/workplaces/invitations/${invitationId}/accept`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  // Decline invitation
  declineInvitation: async (invitationId: string) => {
    const response = await fetch(`${API_BASE_URL}/workplaces/invitations/${invitationId}/decline`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  // Get stats for a specific workplace
  getMyStats: async (businessId: string) => {
    const response = await fetch(`${API_BASE_URL}/workplaces/${businessId}/stats`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  // Get today's bookings
  getTodayBookings: async (businessId: string) => {
    const response = await fetch(`${API_BASE_URL}/workplaces/${businessId}/bookings/today`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  // Get spending wallet
  getMySpendingWallet: async (businessId: string) => {
    const response = await fetch(`${API_BASE_URL}/workplaces/${businessId}/spending`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  // Clock in
  clockIn: async (businessId: string) => {
    const response = await fetch(`${API_BASE_URL}/workplaces/${businessId}/clock-in`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        timestamp: new Date().toISOString(),
        location: null, // Can add GPS if needed
      }),
    });
    return response.json();
  },

  // Clock out
  clockOut: async (businessId: string) => {
    const response = await fetch(`${API_BASE_URL}/workplaces/${businessId}/clock-out`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        timestamp: new Date().toISOString(),
      }),
    });
    return response.json();
  },

  // NOT WIRED (no screen calls this or uploadReceipt). There is no /workplaces/:id/spending/purchase
  // or /receipt route. The real endpoints are POST /staff-purchasing/purchase and
  // /staff-purchasing/purchases/:id/receipt, but they need a staff/business token for that
  // workplace, and a Mango Personal token is refused there. Wire these up once personal tokens are
  // accepted there (or a workplace token exchange exists).
  // Make a purchase (spending wallet)
  makePurchase: async (businessId: string, data: {
    amount: number;
    category: string;
    description: string;
    type: 'mango_pay' | 'virtual_card' | 'reimbursement';
    recipientBusinessId?: string;
  }) => {
    const response = await fetch(`${API_BASE_URL}/workplaces/${businessId}/spending/purchase`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },

  // Upload receipt
  uploadReceipt: async (businessId: string, purchaseId: string, file: File) => {
    const formData = new FormData();
    formData.append('receipt', file);
    
    const token = localStorage.getItem('mango_token');
    const response = await fetch(`${API_BASE_URL}/workplaces/${businessId}/spending/${purchaseId}/receipt`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });
    return response.json();
  },
};

// ==================== LOGISTICS API ====================
// For users tracking shipments and booking rides/deliveries
export const logisticsApi = {
  // Search carriers
  searchCarriers: async (filters: {
    type?: string;
    origin?: string;
    destination?: string;
  }) => {
    const params = new URLSearchParams();
    if (filters.type) params.append('type', filters.type);
    if (filters.origin) params.append('origin', filters.origin);
    if (filters.destination) params.append('destination', filters.destination);
    
    const response = await fetch(`${API_BASE_URL}/logistics/carriers?${params}`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  // Create shipment (parcel, food, ride)
  createShipment: async (data: any) => {
    const response = await fetch(`${API_BASE_URL}/logistics/shipments`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },

  // Track shipment
  trackShipment: async (shipmentId: string) => {
    const response = await fetch(`${API_BASE_URL}/logistics/shipments/${shipmentId}/track`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  // Get my shipments
  getMyShipments: async (page = 1) => {
    const response = await fetch(`${API_BASE_URL}/logistics/shipments?page=${page}`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  // Request a ride
  requestRide: async (data: {
    pickupLocation: { address: string; coordinates: { lat: number; lng: number } };
    dropoffLocation: { address: string; coordinates: { lat: number; lng: number } };
    passengers: number;
    rideType: 'economy' | 'comfort' | 'premium' | 'xl' | 'wheelchair';
  }) => {
    const response = await fetch(`${API_BASE_URL}/logistics/ride/request`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },

  // Order food delivery
  orderFoodDelivery: async (data: {
    restaurantId: string;
    restaurantOrderId: string;
    pickupAddress: any;
    deliveryAddress: any;
    items: any[];
    contactlessDelivery?: boolean;
  }) => {
    const response = await fetch(`${API_BASE_URL}/logistics/food/order`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },

  // Get container rates (for B2B)
  getContainerRates: async (data: {
    originPort: string;
    destinationPort: string;
    containerType: string;
    quantity: number;
  }) => {
    const params = new URLSearchParams();
    params.append('originPort', data.originPort);
    params.append('destinationPort', data.destinationPort);
    params.append('containerType', data.containerType);
    params.append('quantity', data.quantity.toString());
    
    const response = await fetch(`${API_BASE_URL}/logistics/container/rates?${params}`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },
};

// ==================== BUG REPORTS API ====================
export const bugReportsApi = {
  // Submit a detailed bug report
  submit: async (data: {
    title: string;
    description: string;
    category?: string;
    severity?: string;
    steps?: string[];
    expectedBehavior?: string;
    actualBehavior?: string;
    screenshots?: string[];
    errorMessage?: string;
  }) => {
    const response = await fetch(`${API_BASE_URL}/bug-reports`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        ...data,
        pageUrl: window.location.href,
        browserInfo: navigator.userAgent,
        screenSize: `${window.innerWidth}x${window.innerHeight}`,
      }),
    });
    return response.json();
  },

  // Quick submit (minimal info)
  submitQuick: async (data: { message: string; screenshot?: string; currentUrl?: string }) => {
    const response = await fetch(`${API_BASE_URL}/bug-reports/quick`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },

  // Submit via AI chat
  submitViaChat: async (data: { message: string; conversationId: string; context: any }) => {
    const response = await fetch(`${API_BASE_URL}/bug-reports/ai-chat`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },

  // Submit feedback (feature request, suggestion)
  submitFeedback: async (data: {
    type: 'feature_request' | 'improvement' | 'praise' | 'complaint';
    title: string;
    description: string;
    relatedFeature?: string;
    priority?: number;
  }) => {
    const response = await fetch(`${API_BASE_URL}/bug-reports/feedback`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },

  // Get my submitted reports
  getMyReports: async () => {
    const response = await fetch(`${API_BASE_URL}/bug-reports/my/reports`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  // Get single report details
  getReport: async (reportId: string) => {
    const response = await fetch(`${API_BASE_URL}/bug-reports/${reportId}`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },
};

// ==================== BOOKINGS API (Calendar) ====================
export const bookingsApi = {
  list: async (params?: { startDate?: string; endDate?: string; status?: string; page?: number; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.startDate) q.set('startDate', params.startDate);
    if (params?.endDate) q.set('endDate', params.endDate);
    if (params?.status) q.set('status', params.status as string);
    if (params?.page) q.set('page', String(params.page));
    if (params?.limit) q.set('limit', String(params.limit ?? 20));
    const url = `${API_BASE_URL}/bookings${q.toString() ? `?${q.toString()}` : ''}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    return response.json();
  },
  get: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/bookings/${id}`, { headers: getAuthHeaders() });
    return response.json();
  },
};

// ==================== PERSONAL ACTIVITIES API (Time tracking) ====================
export const personalActivitiesApi = {
  list: async () => {
    const response = await fetch(`${API_BASE_URL}/personal-activities`, { headers: getAuthHeaders() });
    return response.json();
  },
  create: async (data: { name: string; frequency: string; durationMinutes: number; color?: string }) => {
    const response = await fetch(`${API_BASE_URL}/personal-activities`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },
  get: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/personal-activities/${id}`, { headers: getAuthHeaders() });
    return response.json();
  },
  update: async (id: string, data: Partial<{ name: string; frequency: string; durationMinutes: number; color: string; isActive: boolean }>) => {
    const response = await fetch(`${API_BASE_URL}/personal-activities/${id}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },
  delete: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/personal-activities/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return response.json();
  },
  start: async (id: string, reminderMinutes?: number) => {
    const response = await fetch(`${API_BASE_URL}/personal-activities/${id}/start`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ reminderMinutes }),
    });
    return response.json();
  },
  stop: async (id: string, notes?: string, sessionId?: string) => {
    const response = await fetch(`${API_BASE_URL}/personal-activities/${id}/stop`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ notes, sessionId }),
    });
    return response.json();
  },
  logSession: async (id: string, data: { startTime: string; endTime?: string; notes?: string }) => {
    const response = await fetch(`${API_BASE_URL}/personal-activities/${id}/sessions`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },
  listSessions: async (params?: { startDate?: string; endDate?: string; activityId?: string }) => {
    const q = new URLSearchParams();
    if (params?.startDate) q.set('startDate', params.startDate);
    if (params?.endDate) q.set('endDate', params.endDate);
    if (params?.activityId) q.set('activityId', params.activityId);
    const url = `${API_BASE_URL}/personal-activities/sessions/list${q.toString() ? `?${q.toString()}` : ''}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    return response.json();
  },
};

// ==================== PERSONAL CALENDAR API ====================
export const personalCalendarApi = {
  getEvents: async (params?: { startDate?: string; endDate?: string }) => {
    const q = new URLSearchParams();
    if (params?.startDate) q.set('startDate', params.startDate);
    if (params?.endDate) q.set('endDate', params.endDate);
    const url = `${API_BASE_URL}/personal-calendar/events${q.toString() ? `?${q.toString()}` : ''}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    return response.json();
  },
  getPersonalEvents: async (params?: { startDate?: string; endDate?: string }) => {
    const q = new URLSearchParams();
    if (params?.startDate) q.set('startDate', params.startDate);
    if (params?.endDate) q.set('endDate', params.endDate);
    const url = `${API_BASE_URL}/personal-calendar/events/me${q.toString() ? `?${q.toString()}` : ''}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    return response.json();
  },
  createEvent: async (data: { title: string; description?: string; location?: string; startTime: string; endTime?: string; color?: string }) => {
    const response = await fetch(`${API_BASE_URL}/personal-calendar/events`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },
  updateEvent: async (id: string, data: Partial<{ title: string; description: string; location: string; startTime: string; endTime: string; color: string }>) => {
    const response = await fetch(`${API_BASE_URL}/personal-calendar/events/${id}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },
  deleteEvent: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/personal-calendar/events/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return response.json();
  },
  checkAvailability: async (inviteeId: string, startTime: string, endTime: string) => {
    const q = new URLSearchParams({ inviteeId, startTime, endTime });
    const response = await fetch(`${API_BASE_URL}/personal-calendar/availability?${q.toString()}`, { headers: getAuthHeaders() });
    return response.json();
  },
  createSharedBooking: async (data: { inviteeId: string; title: string; description?: string; location?: string; startTime: string; endTime: string }) => {
    const response = await fetch(`${API_BASE_URL}/personal-calendar/shared-bookings`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },
  listSharedBookings: async (params?: { startDate?: string; endDate?: string }) => {
    const q = new URLSearchParams();
    if (params?.startDate) q.set('startDate', params.startDate);
    if (params?.endDate) q.set('endDate', params.endDate);
    const url = `${API_BASE_URL}/personal-calendar/shared-bookings${q.toString() ? `?${q.toString()}` : ''}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    return response.json();
  },
  updateSharedBooking: async (id: string, status: 'accepted' | 'declined' | 'cancelled') => {
    const response = await fetch(`${API_BASE_URL}/personal-calendar/shared-bookings/${id}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ status }),
    });
    return response.json();
  },
};

// ==================== HANGOUT INVITES API ====================
export const hangoutInvitesApi = {
  create: async (data: { title?: string; message?: string; proposedSlots: { startTime: string; endTime: string }[]; inviteeIds: string[] }) => {
    const response = await fetch(`${API_BASE_URL}/hangout-invites`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },
  list: async (params?: { status?: string }) => {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    const url = `${API_BASE_URL}/hangout-invites${q.toString() ? `?${q.toString()}` : ''}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    return response.json();
  },
  get: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/hangout-invites/${id}`, { headers: getAuthHeaders() });
    return response.json();
  },
  accept: async (id: string, data: { slotIndex?: number; startTime?: string; endTime?: string }) => {
    const response = await fetch(`${API_BASE_URL}/hangout-invites/${id}/accept`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },
  dismiss: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/hangout-invites/${id}/dismiss`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return response.json();
  },
  proposeChange: async (id: string, data: { startTime: string; endTime: string }) => {
    const response = await fetch(`${API_BASE_URL}/hangout-invites/${id}/propose-change`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },
  respondChange: async (id: string, accept: boolean) => {
    const response = await fetch(`${API_BASE_URL}/hangout-invites/${id}/respond-change`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ accept }),
    });
    return response.json();
  },
  delete: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/hangout-invites/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return response.json();
  },
};

// ==================== ACCOUNTING API (Tax / Finance) ====================
export const accountingApi = {
  overview: async (params?: { startDate?: string; endDate?: string }) => {
    const q = new URLSearchParams();
    if (params?.startDate) q.set('startDate', params.startDate);
    if (params?.endDate) q.set('endDate', params.endDate);
    const url = `${API_BASE_URL}/accounting/overview${q.toString() ? `?${q.toString()}` : ''}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    return response.json();
  },
  transactions: async (params?: { page?: number; perPage?: number }) => {
    const q = new URLSearchParams();
    if (params?.page) q.set('page', String(params.page ?? 1));
    if (params?.perPage) q.set('perPage', String(params.perPage ?? 50));
    const url = `${API_BASE_URL}/accounting/transactions${q.toString() ? `?${q.toString()}` : ''}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    return response.json();
  },
};

// ==================== ASSETS API (Portfolio / Business assets) ====================
export const assetsApi = {
  list: async (params?: { category?: string; status?: string; page?: number }) => {
    const q = new URLSearchParams();
    if (params?.category) q.set('category', params.category);
    if (params?.status) q.set('status', params.status as string);
    if (params?.page) q.set('page', String(params.page ?? 1));
    const url = `${API_BASE_URL}/assets${q.toString() ? `?${q.toString()}` : ''}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    return response.json();
  },
  get: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/assets/${id}`, { headers: getAuthHeaders() });
    return response.json();
  },
};

// ==================== EMPLOYEE PORTAL API (Activity – staff only) ====================
export const portalApi = {
  getActivities: async (params?: { page?: number; limit?: number; activityType?: string; startDate?: string; endDate?: string }) => {
    const q = new URLSearchParams();
    if (params?.page) q.set('page', String(params.page));
    if (params?.limit) q.set('limit', String(params.limit ?? 20));
    if (params?.activityType) q.set('activityType', params.activityType);
    if (params?.startDate) q.set('startDate', params.startDate);
    if (params?.endDate) q.set('endDate', params.endDate);
    const url = `${API_BASE_URL}/portal/activities${q.toString() ? `?${q.toString()}` : ''}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    return response.json();
  },
  logActivity: async (data: { activityType: string; title?: string; description?: string; duration?: number }) => {
    const response = await fetch(`${API_BASE_URL}/portal/activities`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },
};

// ==================== MEDIA LIBRARY API (business-scoped; for Image Sharing when linked) ====================
export const mediaLibraryApi = {
  list: async (params?: { mediaType?: string; category?: string; search?: string; page?: number; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.mediaType) q.set('mediaType', params.mediaType);
    if (params?.category) q.set('category', params.category);
    if (params?.search) q.set('search', params.search);
    if (params?.page) q.set('page', String(params.page ?? 1));
    if (params?.limit) q.set('limit', String(params.limit ?? 24));
    const url = `${API_BASE_URL}/media-library${q.toString() ? `?${q.toString()}` : ''}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    return response.json();
  },
};

// ==================== PROJECTS API (business-scoped; for Collaborative Work when linked) ====================
export const projectsApi = {
  list: async (params?: { status?: string; page?: number }) => {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    if (params?.page) q.set('page', String(params.page ?? 1));
    const url = `${API_BASE_URL}/projects${q.toString() ? `?${q.toString()}` : ''}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    return response.json();
  },
  get: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/projects/${id}`, { headers: getAuthHeaders() });
    return response.json();
  },
};

// ==================== CUSTOMER PROFILE (Business Interactions) API ====================
export const customerProfileApi = {
  getMe: async () => {
    const response = await fetch(`${API_BASE_URL}/customers/me`, { headers: getAuthHeaders() });
    return response.json();
  },
  updateMe: async (data: Record<string, unknown>) => {
    const response = await fetch(`${API_BASE_URL}/customers/me`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },
  getPreferences: async () => {
    const response = await fetch(`${API_BASE_URL}/customers/me/preferences`, { headers: getAuthHeaders() });
    return response.json();
  },
  updatePreferences: async (data: { emailConsent?: boolean; smsConsent?: boolean; notificationPreferences?: Record<string, unknown> }) => {
    const response = await fetch(`${API_BASE_URL}/customers/me/preferences`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },
  getInteractions: async () => {
    const response = await fetch(`${API_BASE_URL}/customers/me/interactions`, { headers: getAuthHeaders() });
    return response.json();
  },
  getInbox: async (category = 'all', limit = 100) => {
    const response = await fetch(
      `${API_BASE_URL}/customers/me/inbox?category=${encodeURIComponent(category)}&limit=${encodeURIComponent(String(limit))}`,
      { headers: getAuthHeaders() }
    );
    return response.json();
  },
};

// ==================== DOCUMENTS API ====================
export interface DocumentItem {
  id: string;
  name: string;
  type: string;
  category?: string | null;
  description?: string | null;
  mediaUrl?: string | null;
  sourceImageUrl?: string | null;
  ocrStatus?: string;
  ocrText?: string | null;
  createdAt?: string;
}
export const documentsApi = {
  list: async (category?: string) => {
    const q = category ? `?category=${encodeURIComponent(category)}` : '';
    const response = await fetch(`${API_BASE_URL}/documents${q}`, { headers: getAuthHeaders() });
    return response.json();
  },
  create: async (data: { name: string; category?: string; description?: string; mediaUrl?: string }) => {
    const response = await fetch(`${API_BASE_URL}/documents`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
    return response.json();
  },
  update: async (id: string, data: Partial<{ name: string; category: string; description: string; mediaUrl: string }>) => {
    const response = await fetch(`${API_BASE_URL}/documents/${encodeURIComponent(id)}`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data) });
    return response.json();
  },
  delete: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/documents/${encodeURIComponent(id)}`, { method: 'DELETE', headers: getAuthHeaders() });
    return response.json();
  },
  scan: async (data: { name?: string; category?: string; sourceImageUrl: string }) => {
    const response = await fetch(`${API_BASE_URL}/documents/scan`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
    return response.json();
  },
};

// ==================== FINANCE API ====================
export interface PersonalTransactionItem {
  id: string;
  amount: number;
  currency: string;
  businessName: string;
  category: string;
  subcategory?: string | null;
  receiptUrl?: string | null;
  taxDeductible: boolean;
  deductionType?: string | null;
  transactionDate: string;
}
export interface PersonalBudgetItem {
  id: string;
  category: string;
  monthlyLimit: number;
  currentSpending: number;
  isActive: boolean;
}
export const financeApi = {
  listTransactions: async (params?: { category?: string; from?: string; to?: string }) => {
    const q = new URLSearchParams();
    if (params?.category) q.set('category', params.category);
    if (params?.from) q.set('from', params.from);
    if (params?.to) q.set('to', params.to);
    const url = `${API_BASE_URL}/finance/transactions${q.toString() ? `?${q.toString()}` : ''}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    return response.json();
  },
  createTransaction: async (data: { amount: number; category: string; businessName?: string; subcategory?: string; receiptUrl?: string; taxDeductible?: boolean; deductionType?: string; transactionDate?: string }) => {
    const response = await fetch(`${API_BASE_URL}/finance/transactions`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
    return response.json();
  },
  deleteTransaction: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/finance/transactions/${encodeURIComponent(id)}`, { method: 'DELETE', headers: getAuthHeaders() });
    return response.json();
  },
  listBudgets: async () => {
    const response = await fetch(`${API_BASE_URL}/finance/budgets`, { headers: getAuthHeaders() });
    return response.json();
  },
  createBudget: async (data: { category: string; monthlyLimit: number }) => {
    const response = await fetch(`${API_BASE_URL}/finance/budgets`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
    return response.json();
  },
  deleteBudget: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/finance/budgets/${encodeURIComponent(id)}`, { method: 'DELETE', headers: getAuthHeaders() });
    return response.json();
  },
  summary: async () => {
    const response = await fetch(`${API_BASE_URL}/finance/summary`, { headers: getAuthHeaders() });
    return response.json();
  },
};

// ==================== TAX AUTOMATION API ====================
export const taxAutomationApi = {
  summary: async (year?: number) => {
    const q = year ? `?year=${year}` : '';
    const response = await fetch(`${API_BASE_URL}/tax-automation/summary${q}`, { headers: getAuthHeaders() });
    return response.json();
  },
  deductibleTransactions: async (year?: number) => {
    const q = year ? `?year=${year}` : '';
    const response = await fetch(`${API_BASE_URL}/tax-automation/deductible-transactions${q}`, { headers: getAuthHeaders() });
    return response.json();
  },
  categorize: async (transactionId: string, data: { taxDeductible: boolean; deductionType?: string }) => {
    const response = await fetch(`${API_BASE_URL}/tax-automation/transactions/${encodeURIComponent(transactionId)}/categorize`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  },
};

// ==================== PORTFOLIO API ====================
export interface PersonalAssetItem {
  id: string;
  name: string;
  assetType: string;
  currentValue: number;
  costBasis?: number | null;
  liability: number;
  notes?: string | null;
}
export interface PersonalIncomeItem {
  id: string;
  source: string;
  incomeType: string;
  grossAmount: number;
  netAmount?: number | null;
  receivedAt: string;
  taxYear: number;
}
export const portfolioApi = {
  listAssets: async () => {
    const response = await fetch(`${API_BASE_URL}/portfolio/assets`, { headers: getAuthHeaders() });
    return response.json();
  },
  createAsset: async (data: { name: string; assetType: string; currentValue: number; costBasis?: number; liability?: number; notes?: string }) => {
    const response = await fetch(`${API_BASE_URL}/portfolio/assets`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
    return response.json();
  },
  deleteAsset: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/portfolio/assets/${encodeURIComponent(id)}`, { method: 'DELETE', headers: getAuthHeaders() });
    return response.json();
  },
  listIncome: async (year?: number) => {
    const q = year ? `?year=${year}` : '';
    const response = await fetch(`${API_BASE_URL}/portfolio/income${q}`, { headers: getAuthHeaders() });
    return response.json();
  },
  createIncome: async (data: { source: string; incomeType?: string; grossAmount: number; netAmount?: number; documentId?: string; receivedAt?: string }) => {
    const response = await fetch(`${API_BASE_URL}/portfolio/income`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
    return response.json();
  },
  deleteIncome: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/portfolio/income/${encodeURIComponent(id)}`, { method: 'DELETE', headers: getAuthHeaders() });
    return response.json();
  },
  netWorth: async () => {
    const response = await fetch(`${API_BASE_URL}/portfolio/net-worth`, { headers: getAuthHeaders() });
    return response.json();
  },
};

// ==================== WISHLIST API ====================
export interface WishlistItem {
  id: string;
  productId?: string | null;
  name: string;
  imageUrl?: string | null;
  url?: string | null;
  note?: string | null;
  priceAtSave?: number | null;
  createdAt?: string;
}
export const wishlistApi = {
  list: async () => {
    const response = await fetch(`${API_BASE_URL}/wishlist`, { headers: getAuthHeaders() });
    return response.json();
  },
  add: async (data: { productId?: string; name: string; imageUrl?: string; url?: string; note?: string; priceAtSave?: number }) => {
    const response = await fetch(`${API_BASE_URL}/wishlist`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
    return response.json();
  },
  remove: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/wishlist/${encodeURIComponent(id)}`, { method: 'DELETE', headers: getAuthHeaders() });
    return response.json();
  },
};

// ==================== PERSONAL QR API ====================

export const qrApi = {
  generate: async () => {
    const response = await fetch(`${API_BASE_URL}/personal/qr/generate`, { method: 'POST', headers: getAuthHeaders() });
    return response.json();
  },
  invalidate: async () => {
    const response = await fetch(`${API_BASE_URL}/personal/qr/invalidate`, { method: 'POST', headers: getAuthHeaders() });
    return response.json();
  },
  getVisibleFields: async () => {
    const response = await fetch(`${API_BASE_URL}/personal/qr/visible-fields`, { headers: getAuthHeaders() });
    return response.json();
  },
  setVisibleFields: async (fields: string[]) => {
    const response = await fetch(`${API_BASE_URL}/personal/qr/visible-fields`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ fields }),
    });
    return response.json();
  },
};

// ==================== PERSONAL COLLABORATION API (Collaborative Work) ====================
export interface PersonalTask {
  id: string;
  title: string;
  isDone: boolean;
  assignedToId?: string | null;
  dueDate?: string | null;
}
export interface PersonalTaskList {
  id: string;
  title: string;
  description?: string | null;
  groupId?: string | null;
  tasks?: PersonalTask[];
}
export const personalCollaborationApi = {
  listLists: async () => {
    const response = await fetch(`${API_BASE_URL}/personal-collaboration/lists`, { headers: getAuthHeaders() });
    return response.json();
  },
  createList: async (data: { title: string; description?: string; groupId?: string }) => {
    const response = await fetch(`${API_BASE_URL}/personal-collaboration/lists`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
    return response.json();
  },
  getList: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/personal-collaboration/lists/${encodeURIComponent(id)}`, { headers: getAuthHeaders() });
    return response.json();
  },
  deleteList: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/personal-collaboration/lists/${encodeURIComponent(id)}`, { method: 'DELETE', headers: getAuthHeaders() });
    return response.json();
  },
  addTask: async (listId: string, data: { title: string; assignedToId?: string; dueDate?: string }) => {
    const response = await fetch(`${API_BASE_URL}/personal-collaboration/lists/${encodeURIComponent(listId)}/tasks`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
    return response.json();
  },
  updateTask: async (id: string, data: Partial<{ title: string; isDone: boolean; assignedToId: string; dueDate: string }>) => {
    const response = await fetch(`${API_BASE_URL}/personal-collaboration/tasks/${encodeURIComponent(id)}`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data) });
    return response.json();
  },
  deleteTask: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/personal-collaboration/tasks/${encodeURIComponent(id)}`, { method: 'DELETE', headers: getAuthHeaders() });
    return response.json();
  },
};

// ==================== FAMILY GROUPS API (chores) ====================
export interface GroupChoreItem {
  id: string;
  groupId: string;
  title: string;
  assignedToId?: string | null;
  frequency: string;
  isDone: boolean;
  dueDate?: string | null;
  rewardAmount?: number | null;
}
export const familyGroupsApi = {
  listChores: async (groupId: string) => {
    const response = await fetch(`${API_BASE_URL}/family-groups/${encodeURIComponent(groupId)}/chores`, { headers: getAuthHeaders() });
    return response.json();
  },
  addChore: async (groupId: string, data: { title: string; assignedToId?: string; frequency?: string; dueDate?: string; rewardAmount?: number }) => {
    const response = await fetch(`${API_BASE_URL}/family-groups/${encodeURIComponent(groupId)}/chores`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
    return response.json();
  },
  completeChore: async (choreId: string) => {
    const response = await fetch(`${API_BASE_URL}/family-groups/chores/${encodeURIComponent(choreId)}/complete`, { method: 'POST', headers: getAuthHeaders() });
    return response.json();
  },
  deleteChore: async (choreId: string) => {
    const response = await fetch(`${API_BASE_URL}/family-groups/chores/${encodeURIComponent(choreId)}`, { method: 'DELETE', headers: getAuthHeaders() });
    return response.json();
  },
};

// ==================== IMAGE ALBUMS API (Image Sharing) ====================
export interface PersonalPhotoItem {
  id: string;
  albumId: string;
  mediaUrl: string;
  caption?: string | null;
}
export interface PersonalAlbumItem {
  id: string;
  title: string;
  description?: string | null;
  coverImageUrl?: string | null;
  sharedWithConnectionIds?: string[];
  photos?: PersonalPhotoItem[];
}
export const imageAlbumsApi = {
  list: async () => {
    const response = await fetch(`${API_BASE_URL}/image-albums`, { headers: getAuthHeaders() });
    return response.json();
  },
  create: async (data: { title: string; description?: string; coverImageUrl?: string }) => {
    const response = await fetch(`${API_BASE_URL}/image-albums`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
    return response.json();
  },
  get: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/image-albums/${encodeURIComponent(id)}`, { headers: getAuthHeaders() });
    return response.json();
  },
  deleteAlbum: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/image-albums/${encodeURIComponent(id)}`, { method: 'DELETE', headers: getAuthHeaders() });
    return response.json();
  },
  addPhoto: async (albumId: string, data: { mediaUrl: string; caption?: string }) => {
    const response = await fetch(`${API_BASE_URL}/image-albums/${encodeURIComponent(albumId)}/photos`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
    return response.json();
  },
  deletePhoto: async (photoId: string) => {
    const response = await fetch(`${API_BASE_URL}/image-albums/photos/${encodeURIComponent(photoId)}`, { method: 'DELETE', headers: getAuthHeaders() });
    return response.json();
  },
  share: async (albumId: string, connectionId: string) => {
    const response = await fetch(`${API_BASE_URL}/image-albums/${encodeURIComponent(albumId)}/share`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ connectionId }) });
    return response.json();
  },
};

// ==================== HELPER FUNCTIONS ====================
function getToken(): string | null {
  return localStorage.getItem('mango_token') || localStorage.getItem('mangoPersonalToken');
}

function getAuthHeaders(): HeadersInit {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

/** For calls that need a specific bearer token (e.g. an exchanged Customer session) instead of
 * the Mango Personal account token — see utils/customerSession.ts. */
export function authHeadersForToken(token: string): HeadersInit {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

// Export all APIs as a single object
export const api = {
  auth: authApi,
  wallet: walletApi,
  marketplace: marketplaceApi,
  b2b: b2bApi,
  messages: messagesApi,
  groups: groupsApi,
  jobs: jobsApi,
  connections: connectionsApi,
  orders: ordersApi,
  tickets: ticketsApi,
  events: eventsApi,
  subscriptions: subscriptionsApi,
  upload: uploadApi,
  eventChat: eventChatApi,
  workplaces: workplacesApi,
  logistics: logisticsApi,
  bugReports: bugReportsApi,
  bookings: bookingsApi,
  personalActivities: personalActivitiesApi,
  personalCalendar: personalCalendarApi,
  accounting: accountingApi,
  assets: assetsApi,
  portal: portalApi,
  mediaLibrary: mediaLibraryApi,
  giftRegistry: giftRegistryApi,
  projects: projectsApi,
  customerProfile: customerProfileApi,
  documents: documentsApi,
  finance: financeApi,
  taxAutomation: taxAutomationApi,
  portfolio: portfolioApi,
  wishlist: wishlistApi,
  qr: qrApi,
  personalCollaboration: personalCollaborationApi,
  familyGroups: familyGroupsApi,
  imageAlbums: imageAlbumsApi,
};

export default api;
